import cspNonceAllowlist from './csp-nonce-allowlist.json';

/// Minimal interface that only includes what we need
interface WindowWithKustomerGetNonce extends Window {
  KustomerGetNonce?: (
    scriptEmbedElement?: HTMLScriptElement | null,
  ) => string | undefined;
}

/**
 * URLs that are allowed to receive CSP nonces when applying nonce to dynamic scripts.
 * These are trusted domains that are safe to apply CSP nonces to.
 *
 * Canonical list: `./csp-nonce-allowlist.json` (also injected into `build/csp-interception.js` at compile time).
 */
export const CSP_NONCE_ALLOWED_URLS: string[] = [...cspNonceAllowlist];

const SCRIPT_EMBED_SELECTOR = '[data-kustomer-api-key]';
const CORE_MAIN_SCRIPT_ID = 'kustomer-core-main';

/**
 * Main function to get the nonce from script tags
 *
 * Resolution order:
 * 1. `window.KustomerGetNonce` when it is a function other than this implementation (integrator override)
 * 2. Explicit `scriptEmbedElement` (when passed) via `readNonceFromScriptElement`
 * 3. `[data-kustomer-api-key]` embed
 * 4. `#kustomer-core-main`
 * 5. `document.currentScript`
 *
 * @param scriptEmbedElement - Optional script element to check for nonce
 * @returns The nonce value if found, undefined otherwise
 */
const readNonceFromScriptElement = (
  el: HTMLScriptElement | null | undefined,
): string | undefined => {
  if (!el) return undefined;
  // Parser-applied CSP nonces are reflected on `.nonce` but may be hidden from
  // `getAttribute('nonce')` in real browsers; prefer the IDL property first.
  const reflected =
    typeof el.nonce === 'string' && el.nonce.trim() !== '' ? el.nonce.trim() : '';
  if (reflected) return reflected;
  const attr = el.getAttribute('nonce');
  return attr && attr.trim() !== '' ? attr.trim() : undefined;
};

export const getNonce = (scriptEmbedElement?: HTMLScriptElement | null): string | undefined => {
  const win =
    typeof window !== 'undefined' ? (window as WindowWithKustomerGetNonce) : undefined;
  const externalGetter = win?.KustomerGetNonce;

  if (typeof externalGetter === 'function' && externalGetter !== getNonce) {
    try {
      const raw = externalGetter(scriptEmbedElement ?? null);
      if (raw != null) {
        const trimmed = String(raw).trim();
        if (trimmed !== '') {
          return trimmed;
        }
      }
    } catch {
      // Integrator-provided KustomerGetNonce threw; continue with built-in resolution.
    }
  }

  if (scriptEmbedElement != null) {
    return readNonceFromScriptElement(scriptEmbedElement);
  }

  const tryScript = (el: Element | null): string | undefined =>
    el instanceof HTMLScriptElement ? readNonceFromScriptElement(el) : undefined;

  const fromDataEmbed = document.querySelector(SCRIPT_EMBED_SELECTOR);
  const fromData = tryScript(fromDataEmbed);
  if (fromData) {
    return fromData;
  }

  const fromCoreMain = document.getElementById(CORE_MAIN_SCRIPT_ID);
  const fromCore = tryScript(fromCoreMain);
  if (fromCore) {
    return fromCore;
  }

  return tryScript(document.currentScript);
};

// Add to window for browser-side files to use without imports (do not clobber a pre-assigned override).
if (typeof window !== 'undefined') {
  const win = window as WindowWithKustomerGetNonce;
  if (!win.KustomerGetNonce) {
    win.KustomerGetNonce = getNonce;
  }
}
